import { ReactNode, useState } from "react";

import { ValueSetExpansionContains } from "@medplum/fhirtypes";

import { Accordion, Box, ScrollArea, Stack, Tabs } from "@mantine/core";
import { FormArrayElement, UseFormReturnType } from "@mantine/form";

import CodingInputWithDetails from "@/components/CodingInputWithDetails";
import { TextWrapper } from "@/components/TextWrapper";
import {
  cptQuickPickerSections,
  icd10MostCommonValueSets,
  MostCommonSectionConfig,
  PROCEDURES_PICKER_TABS,
} from "@/encounters/constants/procedures";
import { useProceduresPickerValueSet } from "@/encounters/hooks/useProceduresPickerValueSet";
import { ProceduresPickerFormType } from "@/encounters/zod-schemas/procedures";
import { ICD10ValueSets } from "@/fhir/enums/icd";
import { usePracticeId } from "@/hooks/usePracticeId";
import { isProcedureCodeOfferedOn, ServiceDateKey } from "@/shared/procedure-code-effective-dates";
import { CptCodesHelpText } from "@/shared/procedure-codes";

import MostCommonSection, {
  getMostCommonSectionId,
  ProceduresPickerField,
} from "./MostCommonSection";
import classes from "./ProceduresPicker.module.css";

const buildConditionEntry = (
  coding: ValueSetExpansionContains,
): FormArrayElement<ProceduresPickerFormType, "conditions"> => ({
  system: coding.system ?? "",
  code: coding.code ?? "",
  display: coding.display ?? "",
});

const buildProcedureEntry = (
  coding: ValueSetExpansionContains,
): FormArrayElement<ProceduresPickerFormType, "procedures"> => ({
  ...buildConditionEntry(coding),
  billerNotes: "",
  providerNotes: "",
  units: 1,
  modifiers: [],
});

const accordionStyles = {
  item: { border: "none", borderBottom: "1px solid var(--mantine-color-gray-2)" },
  label: { fontWeight: 600, fontSize: 14 },
  control: { paddingLeft: 0, paddingRight: 0 },
  content: { paddingLeft: 0, paddingRight: 0 },
};

const panelStyle = {
  flex: 1,
  display: "flex" as const,
  flexDirection: "column" as const,
  overflow: "hidden",
  paddingTop: 16,
};

type ProceduresPickerTabConfig<K extends ProceduresPickerField> = {
  tab: PROCEDURES_PICKER_TABS;
  label: string;
  name: K;
  placeholder: string;
  valueSetUrl: string;
  disabled?: boolean;
  /** Holds only the search input, while its ValueSet is still being built. */
  isSearchPending?: boolean;
  sections: MostCommonSectionConfig[];
  /** Shown under the search input, for example what the listed codes cover. */
  helpText?: ReactNode;
  buildEntry: (coding: ValueSetExpansionContains) => FormArrayElement<ProceduresPickerFormType, K>;
  /** Offers only the codes it returns true for, in the sections and, by default, the search. */
  filterOption?: (coding: ValueSetExpansionContains) => boolean;
  /** Offers only the search matches it returns true for, in place of filterOption. */
  searchFilterOption?: (coding: ValueSetExpansionContains) => boolean;
  /** Search empty state when the search filter hid every match. */
  filteredOutMessage?: string;
};

type ProceduresPickerTabProps<K extends ProceduresPickerField> = ProceduresPickerTabConfig<K> & {
  form: UseFormReturnType<ProceduresPickerFormType>;
};

/**
 * One picker tab: a code search input plus an accordion of "most common"
 * sections. Each section loads its codes lazily on first open, so opening the
 * picker doesn't fire a request per section up front.
 */
function ProceduresPickerTab<K extends ProceduresPickerField>({
  form,
  tab,
  name,
  placeholder,
  valueSetUrl,
  disabled = false,
  isSearchPending = false,
  sections,
  helpText,
  buildEntry,
  filterOption,
  searchFilterOption = filterOption,
  filteredOutMessage,
}: ProceduresPickerTabProps<K>) {
  const [openSections, setOpenSections] = useState<string[]>([]);

  return (
    <Tabs.Panel value={tab} style={panelStyle}>
      <CodingInputWithDetails
        multiple
        form={form}
        name={name}
        buildEntry={buildEntry}
        placeholder={placeholder}
        valueSetUrl={valueSetUrl}
        filterOption={searchFilterOption}
        disabled={disabled || isSearchPending}
        filteredOutMessage={filteredOutMessage}
        data-testid={`procedures-picker-${name}-input`}
      />
      {helpText && <Box mt="xs">{helpText}</Box>}
      <ScrollArea flex={1} offsetScrollbars>
        <Accordion
          multiple
          value={openSections}
          styles={accordionStyles}
          onChange={setOpenSections}
          data-testid={`${name}-most-common-accordion`}
        >
          {sections.map((section) => (
            <MostCommonSection
              form={form}
              name={name}
              section={section}
              disabled={disabled}
              buildEntry={buildEntry}
              filterOption={filterOption}
              key={getMostCommonSectionId(section)}
              opened={openSections.includes(getMostCommonSectionId(section))}
            />
          ))}
        </Accordion>
      </ScrollArea>
    </Tabs.Panel>
  );
}

function ProceduresPickerLeftPanel({
  form,
  conditionsLocked = false,
  proceduresLocked = false,
  serviceDate,
}: {
  form: UseFormReturnType<ProceduresPickerFormType>;
  /** The picker opened on a single diagnosis, so the diagnosis selection cannot be changed. */
  conditionsLocked?: boolean;
  /** The picker opened on a single procedure code, so the procedure selection cannot be changed. */
  proceduresLocked?: boolean;
  /** The encounter's service date (`yyyy-MM-dd`); codes not billable on it are not offered. */
  serviceDate?: ServiceDateKey;
}) {
  const practiceId = usePracticeId();
  const {
    valueSetUrl: cptValueSetUrl,
    isReady: isCptValueSetReady,
    activeCodes,
    uncheckedCategories,
  } = useProceduresPickerValueSet(practiceId);
  const uncheckedCodeSystems = cptQuickPickerSections.reduce<Set<string>>(
    (codeSystems, section) => {
      if (uncheckedCategories?.has(section.title)) codeSystems.add(section.codeSystem);
      return codeSystems;
    },
    new Set(),
  );
  const [activeTab, setActiveTab] = useState<PROCEDURES_PICKER_TABS>(PROCEDURES_PICKER_TABS.ICD10);

  const handleTabVariant = (tab: PROCEDURES_PICKER_TABS) =>
    activeTab === tab ? "bold" : "default";

  const icd10Tab: ProceduresPickerTabConfig<"conditions"> = {
    tab: PROCEDURES_PICKER_TABS.ICD10,
    label: "ICD-10 Diagnoses",
    name: "conditions",
    placeholder: "Search ICD-10s",
    valueSetUrl: ICD10ValueSets.Clinical,
    disabled: conditionsLocked,
    sections: icd10MostCommonValueSets,
    buildEntry: buildConditionEntry,
  };

  const cptTab: ProceduresPickerTabConfig<"procedures"> = {
    tab: PROCEDURES_PICKER_TABS.CPT,
    label: "CPT Codes",
    name: "procedures",
    placeholder: "Search CPT Codes",
    valueSetUrl: cptValueSetUrl,
    disabled: proceduresLocked,
    isSearchPending: !isCptValueSetReady,
    sections: cptQuickPickerSections,
    helpText: <CptCodesHelpText context="picker" />,
    buildEntry: buildProcedureEntry,
    filterOption: (coding) => {
      const code = coding.code ?? "";
      // The quick picker lists the shared fee schedule, so codes the practice turned off are
      // hidden here. Until the practice's codes are known nothing is listed; if they can't be
      // loaded, every code is listed, for the whole tab or for the categories left unchecked.
      const isActiveForPractice =
        isCptValueSetReady &&
        (!activeCodes || activeCodes.has(code) || uncheckedCodeSystems.has(coding.system ?? ""));
      return isActiveForPractice && isProcedureCodeOfferedOn(code, serviceDate);
    },
    // The search already leaves out codes the practice turned off, and offers CPT codes outside
    // the fee schedule, so a match is hidden here only for its service date.
    searchFilterOption: (coding) => isProcedureCodeOfferedOn(coding.code ?? "", serviceDate),
    filteredOutMessage: "The matching codes aren't billable on this encounter's service date.",
  };

  return (
    <Stack p="md" gap={0} flex={1} style={{ overflow: "hidden" }}>
      <Tabs
        radius="md"
        color="gray"
        variant="pills"
        value={activeTab}
        data-testid="procedures-picker-tabs"
        onChange={(tab) => setActiveTab(tab as PROCEDURES_PICKER_TABS)}
        style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}
      >
        <Tabs.List className={classes.tabsList}>
          {[icd10Tab, cptTab].map(({ tab, label }) => (
            <Tabs.Tab key={tab} value={tab} className={classes.tab} data-testid={`${tab}-tab`}>
              <TextWrapper variant={handleTabVariant(tab)}>{label}</TextWrapper>
            </Tabs.Tab>
          ))}
        </Tabs.List>

        <ProceduresPickerTab form={form} {...icd10Tab} />
        <ProceduresPickerTab form={form} {...cptTab} />
      </Tabs>
    </Stack>
  );
}

export default ProceduresPickerLeftPanel;
